import React, { useState, useEffect, useRef } from 'react';
import Connectors from '../components/settings/Connectors';
import UserAdmin from '../components/settings/UserAdmin';
import AuditLog from '../components/settings/AuditLog';
import Trash from '../components/settings/Trash';
import DataSources from '../components/settings/DataSources';
import { Icon } from '../components/settings/icons';
import '../styles/settings.css';

// Settings is the admin console: external-system connectors, user admin,
// audit trail, trash recovery, and data source management.
// Panels are mounted one at a time. Each owns its own fetching.
//
// Left-hand section nav (not top tabs) — five sections is enough that a
// horizontal bookmark row was already tight, and Connectors alone (see
// components/settings/Connectors.tsx) grew its own nested list+detail nav
// for the same reason. A vertical rail scales to more sections without
// wrapping or scrolling sideways.

interface TabDef {
  id: string;
  label: string;
  icon: string;
  desc: string;
  adminOnly?: boolean;
}

const TABS: TabDef[] = [
  {
    id: 'connectors',
    label: 'Connectors',
    icon: 'plug',
    desc: 'External system connectors · S3 · Jira · Keycloak. Secrets are stored server-side and never shown back.',
  },
  {
    id: 'users',
    label: 'Users & groups',
    icon: 'users',
    desc: 'Provision teammates, set what each person can see, and bundle common access into reusable groups.',
    adminOnly: true,
  },
  {
    id: 'audit',
    label: 'Audit trail',
    icon: 'history',
    desc: 'Every connector save, user and group change, restore and purge, newest first. Append-only and read-only — times are UTC.',
    adminOnly: true,
  },
  {
    id: 'trash',
    label: 'Trash',
    icon: 'trash',
    desc: 'Soft-deleted projects and collection items. Restore returns one to service; purge destroys it permanently.',
    adminOnly: true,
  },
  {
    id: 'datasources',
    label: 'Data sources',
    icon: 'database',
    desc: 'Connections, bindings, the 16 editable data tables that feed portfolio analytics, and ingest pipeline health.',
    adminOnly: true,
  },
];

const TAB_KEY = 'atlas.settings.tab';

function rememberedTab(valid: string[]): string {
  try {
    // A URL tab= param (e.g. the redirect back from a Microsoft OAuth
    // consent flow) wins over whatever was last remembered in this tab.
    const fromUrl = new URLSearchParams(window.location.search).get('tab');
    if (fromUrl && valid.includes(fromUrl)) return fromUrl;
    const v = sessionStorage.getItem(TAB_KEY);
    return v && valid.includes(v) ? v : valid[0];
  } catch {
    return valid[0];
  }
}

export function SettingsPage() {
  // Admin check — in Atlas, this could come from auth context. For now, default to true
  // so all tabs are visible. The server gates routes independently.
  const isAdmin = true;
  const tabs = TABS.filter((t) => !t.adminOnly || isAdmin);
  const ids = tabs.map((t) => t.id);

  const [tab, setTab] = useState(() => rememberedTab(TABS.map((t) => t.id)));
  const current = tabs.find((t) => t.id === tab) || tabs[0];

  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const panelRef = useRef<HTMLDivElement | null>(null);
  const didMountRef = useRef(false);

  useEffect(() => {
    if (!current) return;
    try { sessionStorage.setItem(TAB_KEY, current.id); } catch { /* private mode */ }
    if (!didMountRef.current) { didMountRef.current = true; return; }
    if (panelRef.current) panelRef.current.focus();
  }, [current]);

  function onKeyDown(e: React.KeyboardEvent) {
    if (!ids.length) return;
    const i = ids.indexOf(current.id);
    let ni = i;
    switch (e.key) {
      case 'ArrowDown': ni = (i + 1) % ids.length; break;
      case 'ArrowUp': ni = (i - 1 + ids.length) % ids.length; break;
      case 'Home': ni = 0; break;
      case 'End': ni = ids.length - 1; break;
      default: return;
    }
    e.preventDefault();
    setTab(ids[ni]);
    const el = tabRefs.current[ids[ni]];
    if (el) el.focus();
  }

  const tabId = (id: string) => `stg-tab-${id}`;
  const panelId = (id: string) => `stg-panel-${id}`;

  return (
    <>
      <div className="pagehead">
        <div className="pagehead__row">
          <div className="pagehead__text">
            <p className="pagehead__eyebrow">Admin console · connectors, users, audit, trash</p>
            <h1 className="pagehead__title">Settings</h1>
            <div className="pagehead__facts">
              <span className="fact">{current?.desc ? current.label : 'Settings'}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="pagebody">
        <div className="stg-shell">
          {tabs.length > 1 ? (
            <nav className="stg-shell-nav" role="tablist" aria-label="Settings sections" aria-orientation="vertical" onKeyDown={onKeyDown}>
              {tabs.map((t) => {
                const isActive = current && t.id === current.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    role="tab"
                    id={tabId(t.id)}
                    aria-selected={!!isActive}
                    aria-controls={isActive ? panelId(t.id) : undefined}
                    tabIndex={isActive ? 0 : -1}
                    ref={(el) => { tabRefs.current[t.id] = el; }}
                    className="stg-shell-item"
                    onClick={() => setTab(t.id)}
                  >
                    <Icon name={t.icon} size={15} />
                    <span className="stg-shell-item-label">{t.label}</span>
                  </button>
                );
              })}
            </nav>
          ) : null}

          <div className="stg-shell-main">
            <p className="stg-desc" style={{ maxWidth: '78ch', marginBottom: 'var(--stg-s4)' }}>
              {current ? current.desc : null}
            </p>

            {current ? (
              <div
                role="tabpanel"
                id={panelId(current.id)}
                aria-labelledby={tabId(current.id)}
                tabIndex={-1}
                ref={panelRef}
                className="stg-panel"
              >
                {current.id === 'connectors' ? <Connectors /> : null}
                {current.id === 'users' ? <UserAdmin /> : null}
                {current.id === 'audit' ? <AuditLog /> : null}
                {current.id === 'trash' ? <Trash /> : null}
                {current.id === 'datasources' ? <DataSources isAdmin={isAdmin} /> : null}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}

export default SettingsPage;
